export const displayValue = (value: number | null | undefined): string | number => {
  return value === null || value === undefined ? "—" : value;
};

export const toText = (value: unknown, fallback = "N/A"): string => {
  if (value === null || value === undefined || value === "") return fallback;
  return String(value);
};

export const toArray = <T,>(value: unknown): T[] => {
  return Array.isArray(value) ? (value as T[]) : [];
};

export const getNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

export const formatExperience = (experience: unknown): string => {
  if (
    experience === null ||
    experience === undefined ||
    String(experience).trim() === ""
  ) {
    return "N/A";
  }

  return String(experience);
};

export const formatDate = (value: unknown): string => {
  if (!value) return "N/A";

  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const formatMonthYear = (value: unknown): string => {
  if (!value) return "N/A";

  const raw = String(value);
  const date = new Date(raw.length === 7 ? `${raw}-01` : raw);
  if (Number.isNaN(date.getTime())) return raw;

  return date.toLocaleDateString("en-IN", {
    month: "short",
    year: "numeric",
  });
};

export const calculateDuration = (
  startValue: unknown,
  endValue: unknown,
  currentlyWorking: boolean
): string => {
  if (!startValue) {
    return "N/A";
  }

  const parseResumeDate = (
    value: unknown
  ): Date | null => {
    if (!value) {
      return null;
    }

    const raw = String(value).trim();

    if (!raw) {
      return null;
    }

    // Normalize full month names and common abbreviations
    const normalized = raw
      .replace(/\bJanuary\b/gi, "Jan")
      .replace(/\bFebruary\b/gi, "Feb")
      .replace(/\bMarch\b/gi, "Mar")
      .replace(/\bApril\b/gi, "Apr")
      .replace(/\bMay\b/gi, "May")
      .replace(/\bJune\b/gi, "Jun")
      .replace(/\bJuly\b/gi, "Jul")
      .replace(/\bAugust\b/gi, "Aug")
      .replace(/\bSeptember\b/gi, "Sep")
      .replace(/\bSept\b/gi, "Sep")
      .replace(/\bOctober\b/gi, "Oct")
      .replace(/\bNovember\b/gi, "Nov")
      .replace(/\bDecember\b/gi, "Dec");

    // YYYY-MM
    if (/^\d{4}-\d{2}$/.test(normalized)) {
      const date = new Date(
        `${normalized}-01T00:00:00`
      );

      return Number.isNaN(date.getTime())
        ? null
        : date;
    }

    const monthYearMatch =
      normalized.match(
        /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*[\s,-]+(\d{4})$/i
      );

    if (monthYearMatch) {
      const monthMap: Record<string, number> = {
        jan: 0,
        feb: 1,
        mar: 2,
        apr: 3,
        may: 4,
        jun: 5,
        jul: 6,
        aug: 7,
        sep: 8,
        oct: 9,
        nov: 10,
        dec: 11,
      };

      const monthKey = monthYearMatch[1]
        .slice(0, 3)
        .toLowerCase();

      const month = monthMap[monthKey];
      const year = Number(monthYearMatch[2]);

      if (
        month !== undefined &&
        Number.isFinite(year)
      ) {
        return new Date(
          year,
          month,
          1
        );
      }
    }

    // YYYY
    if (/^\d{4}$/.test(normalized)) {
      const date = new Date(
        Number(normalized),
        0,
        1
      );

      return Number.isNaN(date.getTime())
        ? null
        : date;
    }

    // Fallback for normal date formats
    const date = new Date(normalized);

    return Number.isNaN(date.getTime())
      ? null
      : date;
  };

  // Start date
  const start = parseResumeDate(
    startValue
  );

  if (!start) {
    return "N/A";
  }

  // End date
  // If currently working, calculate until today.
  const end = currentlyWorking
    ? new Date()
    : parseResumeDate(endValue);

  if (!end) {
    return "N/A";
  }

  if (end < start) {
    return "N/A";
  }

  // Calculate calendar-month difference
  let months =
    (end.getFullYear() -
      start.getFullYear()) *
    12 +
    (end.getMonth() -
      start.getMonth());

  months = Math.max(0, months);

  // Convert months into years + months
  const years = Math.floor(
    months / 12
  );

  const remainingMonths =
    months % 12;

  if (years === 0) {
    return `${remainingMonths} month${remainingMonths === 1
        ? ""
        : "s"
      }`;
  }

  if (remainingMonths === 0) {
    return `${years} year${years === 1
        ? ""
        : "s"
      }`;
  }

  return `${years} year${years === 1
      ? ""
      : "s"
    } ${remainingMonths} month${remainingMonths === 1
      ? ""
      : "s"
    }`;
};

export const getProfileUrl = (value: string): string => {
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
};

export const getFileSize = (bytes: unknown): string => {
  const size = getNumber(bytes);
  if (size === null || size < 0) return "N/A";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

export const getInitials = (name: string, fallback = "Candidate"): string => {
  return String(name || fallback)
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
};

export const flattenSkills = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map((item: unknown) => String(item));

  if (value && typeof value === "object") {
    return Object.values(value as Record<string, unknown>).flatMap((item: unknown) => {
      if (Array.isArray(item)) return item.map((skill: unknown) => String(skill));
      return item ? [String(item)] : [];
    });
  }

  return [];
};
